import { cloneAsync } from "./deepCloner";
import persist, { persistType } from "./persist";

class TreePos {
   own = null;
   parent = null;
   children = [];
   level = 0;
   get HasChildren() {
      return this.children.length > 0;
   }
}

export class TreeMap {
   _lastkey = 0;
   get NewKey() {
      return ++this._lastkey;
   }
   /**
    * Get elements with no parents
    */
   get Roots() {
      let rtns = [];
      for (var prop in this) {
         if (this.hasOwnProperty(prop)) {
            if (Number.isInteger(parseInt(prop))) {
               let item = this[prop];
               if (item._tree.parent === null) {
                  rtns.push(item);
               }
            }
         }
      }
      return rtns;
   }

   /**
    * Set returnNeg1IfNotFound to `true` if using to find parent for `TreeFlattener.Flatten()`
    * @param {function} selector
    */
   First(selector, returnNeg1IfNotFound = false) {
      for (var prop in this) {
         if (this.hasOwnProperty(prop)) {
            if (Number.isInteger(parseInt(prop))) {
               let item = this[prop];
               if (selector(item)) {
                  return item;
               }
            }
         }
      }
      return returnNeg1IfNotFound ? -1 : null;
   }

   /**
    *
    * @param {function} selector
    */
   Where(selector) {
      let rtns = [];
      for (var prop in this) {
         if (this.hasOwnProperty(prop)) {
            if (Number.isInteger(parseInt(prop))) {
               let item = this[prop];
               if (selector(item)) {
                  rtns.push(item);
               }
            }
         }
      }
      return rtns;
   }

   /**
    * Get all elements
    */
   get Values() {
      let rtns = [];
      for (var prop in this) {
         if (this.hasOwnProperty(prop)) {
            if (Number.isInteger(parseInt(prop))) {
               rtns.push(this[prop]);
            }
         }
      }
      return rtns;
   }

   /**
    * Remove an item and all its children recursivly
    * @param {*} item
    */
   DeleteBranch(item) {
      if (item._tree.parent !== null) {
         let parentItem = this[item._tree.parent];
         if (parentItem) {
            parentItem._tree.children = parentItem._tree.children.filter(x => x !== item._tree.own);
         }
      }

      const delChildren = parent => {
         if (parent) {
            parent._tree.children.forEach(x => {
               delChildren(this[x]);
            });
            delete this[parent._tree.own];
         }
      };
      delChildren(item);
   }

   /**
    * Remove all items and their children recursivly
    * @param {Array} items
    */
   DeleteBranchs(items) {
      for (let i = 0; i < items.length; i++) {
         const element = items[i];
         this.DeleteBranch(element);
      }
   }

   /**
    * Returns the root element for this item
    * @param {object} child Child Item
    */
   GetRootFor(child){
      while(child._tree.parent !== null){
         child = this[child._tree.parent];
      }
      return child;
   }

   /**
    * Get Children nodes for parent Node
    * @param {object} parent Node
    */
   GetChildrenFor(parent){
      let rtns = [];
      parent._tree.children.forEach(x => rtns.push(this[x]));
      return rtns;
   }
}

function flattenItem(dictionary, parent, item) {
   item._tree = new TreePos();
   item._tree.own = dictionary.NewKey;
   item._tree.parent = parent ? parent._tree.own : null;
   if (parent) {
      parent._tree.children.push(item._tree.own);
      item._tree.level = parent._tree.level + 1;
   }
}

function flattenChildren(dictionary, parent, childrenSelector, recurseCount) {
   let data = childrenSelector(parent);
   //when count reaches zero stop flattening
   recurseCount--;
   for (let i = 0; data && i < data.length; i++) {
      const item = data[i];

      flattenItem(dictionary, parent, item);

      if (recurseCount !== 0) {
         flattenChildren(dictionary, item, childrenSelector, recurseCount);
      }

      dictionary[item._tree.own] = item;
   }
}

function toTree(dictionary, parent, childrenSelector, recurse) {
   const selector = childrenSelector(parent);
   parent[selector] = [];

   for (let i = 0; i < parent._tree.children.length; i++) {
      const index = parent._tree.children[i];
      let child = dictionary[index];
      if (recurse) {
         toTree(dictionary, child, childrenSelector, recurse);
      }
      parent[selector].push(child);
   }
}


/**
 * Turns a tree based structure into a flat map structure with ids pointing to parents/children
 */
class Flattener {
   /**
    * Flatten a tree structure based on a given funciton pointing to the children array.
    * Pass a parent object and an existing map to map children to that object
    * @param {*} data Array or object
    * @param {function} childrenSelector Lambda pointing to children prop. Redundunt if recurseCount == 0
    * @param {number} recurseCount Number of levels to flatten
    * @param {object} parent Optional parent object to specify as root for adding more data to an existing map
    * @param {TreeMap} dictionary Existing map to add more data to
    */
   Flatten(data, childrenSelector, recurseCount = null, parent = null, dictionary = new TreeMap()) {
      if (parent === -1) {
         //dont add this item if we tried to find parent but failed
         return;
      }
      const action = item => {
         flattenItem(dictionary, parent, item);
         if (childrenSelector && (recurseCount === null || recurseCount !== 0)) {
            recurseCount = recurseCount || -1;
            flattenChildren(dictionary, item, childrenSelector, recurseCount);
         }
         dictionary[item._tree.own] = item;
      };

      if (Array.isArray(data)) {
         for (let i = 0; i < data.length; i++) {
            const item = data[i];
            action(item);
         }
      } else {
         action(data);
      }
      return dictionary;
   }

   /**
    * Revert a TreeMap into a tree structure
    * @param {TreeMap} dictionary TreeMap
    * @param {function} childrenSelector Lambda returning string name of children prop
    * @param {*} parentId Optional tree id to map from
    * @param {*} recurse
    */
   ToTree(dictionary, childrenSelector, parentId = null, recurse = true) {
      let rtns = [];

      for (var prop in dictionary) {
         if (dictionary.hasOwnProperty(prop)) {
            if (Number.isInteger(parseInt(prop))) {
               let item = dictionary[prop];
               if (item._tree.parent === parentId) {
                  rtns.push(item);
                  toTree(dictionary, item, childrenSelector, recurse);
               }
            }
         }
      }

      return rtns.length == 1 ? rtns[0] : rtns;
   }

   /**
    * Copy an existing branch to a new or existing map and maintian its children hierarchy.
    * Because items are stored in a map by referenc only, simply copying one item to anotehr map will break
    * the existing map, so use this to create copies
    * @param {object} nodeToCopy Parent node to copy + all its children
    * @param {TreeMap} copyFromDictionary Existing map
    * @param {TreeMap} copyToDictionary Copy to map
    * @param {object} copyUnder Existing node in copyToDictionary to make copied object a child
    */
   async CopyBranchAsync(nodeToCopy, copyFromDictionary, copyToDictionary = new TreeMap(), copyUnder = null) {
      const copyItems = async (parent, current, kidIndicesToCopy) => {
         const _clone = await cloneAsync(current);
         this.Flatten(_clone, null, 0, parent, copyToDictionary);
         for (let i = 0; i < kidIndicesToCopy.length; i++) {
            const index = kidIndicesToCopy[i];
            const kid = copyFromDictionary[index];
            await copyItems(_clone, kid, kid._tree.children);
         }
      };

      await copyItems(copyUnder, nodeToCopy, nodeToCopy._tree.children);

      return copyToDictionary;
   }
}

const inst = new Flattener();

export default {
   CopyBranchAsync: Flattener.prototype.CopyBranchAsync.bind(inst),
   ToTree: Flattener.prototype.ToTree.bind(inst),
   Flatten: Flattener.prototype.Flatten.bind(inst)
};


TreeMap = persistType("TreeMap", TreeMap);
TreePos = persistType("TreePos", TreePos);


// WEBPACK FOOTER //
// ./src/utils/reduxTreeFlatener.js